import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  RiDashboardFill,
  RiFileExcel2Fill,
  RiServiceFill,
  RiVideoChatFill,
  RiChat3Line
} from "react-icons/ri";
import { AudioOutlined } from "@ant-design/icons";
import { GiArtificialIntelligence, GiOfficeChair, GiBuyCard } from "react-icons/gi";
import { FaPerson, FaUserClock, FaDiagramPredecessor } from "react-icons/fa6";
import { FaSuitcase, FaUmbrellaBeach } from "react-icons/fa";
import { BiSpreadsheet } from "react-icons/bi";
import {
  MdOutlineSecurity,
  MdMiscellaneousServices,
  MdSupportAgent,
  MdOutlineWarehouse,
  MdOutlineInventory2,
  MdOutlineEmail,
  MdOutlineReceiptLong,
  MdOutlineMeetingRoom,
  MdOutlineSquareFoot
} from "react-icons/md";
import { SiNginxproxymanager, SiGithubactions } from "react-icons/si";
import {
  LiaFileContractSolid,
  LiaProjectDiagramSolid,
  LiaFileInvoiceDollarSolid
} from "react-icons/lia";
import { BsBank } from "react-icons/bs";
import {
  TbZoomMoney,
  TbReportMoney,
  TbReceiptTax,
  TbFileExport,
  TbRepeat,
  TbClipboardCheck,
  TbCameraPlus,
  TbFilePlus,
  TbTool,
  TbNotebook,
  TbUserShield,
  TbPackages
} from "react-icons/tb";
import { TbPigMoney } from "react-icons/tb";
import { AiOutlineFundProjectionScreen, AiOutlineControl } from "react-icons/ai";
import { TbShieldCheck, TbSchool } from "react-icons/tb";
import { GoTasklist } from "react-icons/go";
import { IoIdCardOutline, IoDocumentLockSharp } from "react-icons/io5";
import { RiExchangeFundsFill } from "react-icons/ri";
import { TbStar, TbMessage2 } from "react-icons/tb";
import { TbTruckDelivery, TbCalendarUser } from "react-icons/tb";
import "./sidebar.css";
import { Menu } from "antd";
import { useTranslation } from "react-i18next";
import { hasPermission } from "../../../commons/Permissions/PermissionsHook";
import axios from "axios";
import { isAuthenticated } from "../../../commons/Auth/auth";

// The drawer menu and the mobile bottom bar both need the employee's permissions; share one
// request between them instead of fetching per consumer.
let employeeRequest = null;
const loadEmployee = employeeId => {
  if (employeeRequest?.employeeId !== employeeId) {
    const promise = axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/employees/employee/${employeeId}`, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      })
      .then(res => (res.status === 200 || res.status === 304 ? res.data : {}))
      .catch(err => {
        employeeRequest = null;
        console.error(err);
        return {};
      });
    employeeRequest = { employeeId, promise };
  }
  return employeeRequest.promise;
};

/** Service-provider navigation, filtered to what the signed-in user may read. */
export const useServiceProviderMenu = () => {
  const { t, i18n } = useTranslation("dashboard");
  const [userData, setUserData] = useState({});
  const [menuItems, setMenuItems] = useState([]);

  const items = [
    {
      key: "Dashboard",
      className: "ant-menu-custom-item",
      icon: <RiDashboardFill />,
      permission: "Dashboard",
      label: (
        <Link to="/user">
          <span>{t("Dashboard")}</span>
        </Link>
      )
    },
    // Every staff member's own jobs today/tomorrow (no permission: the server shows only one's own)
    {
      key: "My-day",
      className: "ant-menu-custom-item",
      icon: <TbCalendarUser />,
      label: (
        <Link to="/user/my-day">
          <span>{t("My day")}</span>
        </Link>
      )
    },
    {
      key: "Meetings",
      className: "ant-menu-custom-item",
      icon: <RiVideoChatFill />,
      permission: "Meetings",
      label: (
        <Link to="/user/meetings">
          <span>{t("Meetings")}</span>
        </Link>
      )
    },
    {
      key: "Mail",
      className: "ant-menu-custom-item",
      icon: <MdOutlineEmail />,
      permission: "Mail",
      label: (
        <Link to="/user/mail">
          <span>{t("Mail")}</span>
        </Link>
      )
    },
    {
      key: "Chat",
      className: "ant-menu-custom-item",
      icon: <RiChat3Line />,
      permission: "Chat",
      label: (
        <Link to="/chats">
          <span>{t("Chat")}</span>
        </Link>
      )
    },
    {
      key: "Human Resources",
      className: "ant-menu-custom-parent-item",
      icon: <FaPerson />,
      label: <span>{t("Human Resources")}</span>,
      children: [
        {
          key: "Employees",
          className: "ant-submenu-custom-item",
          icon: <FaSuitcase />,
          permission: "Employees",
          label: (
            <Link to="/user/employees">
              <span>{t("Employees")}</span>
            </Link>
          )
        },
        {
          key: "Attendance",
          className: "ant-submenu-custom-item",
          icon: <BiSpreadsheet />,
          permission: "Attendance",
          label: (
            <Link to="/user/attendance">
              <span>{t("Attendance")}</span>
            </Link>
          )
        },
        {
          key: "Leaves",
          className: "ant-submenu-custom-item",
          icon: <FaUmbrellaBeach />,
          permission: "Leaves",
          label: (
            <Link to="/user/leaves">
              <span>{t("Leaves")}</span>
            </Link>
          )
        },
        {
          key: "Designations",
          permission: "Designations",
          className: "ant-submenu-custom-item",
          icon: <SiNginxproxymanager />,
          label: (
            <Link to="/user/designations">
              <span>{t("Designations")}</span>
            </Link>
          )
        },
        {
          key: "Departments",
          permission: "Departments",
          className: "ant-submenu-custom-item",
          icon: <GiOfficeChair />,
          label: (
            <Link to="/user/departments">
              <span>{t("Departments")}</span>
            </Link>
          )
        },
        {
          key: "Contracts",
          permission: "Contracts",
          className: "ant-submenu-custom-item",
          icon: <LiaFileContractSolid />,
          label: (
            <Link to="/user/contracts">
              <span>{t("Contracts")}</span>
            </Link>
          )
        },
        {
          key: "Safety",
          permission: "Safety",
          className: "ant-submenu-custom-item",
          icon: <TbShieldCheck />,
          label: (
            <Link to="/user/safety">
              <span>{t("Occupational safety")}</span>
            </Link>
          )
        },
        {
          key: "Apprenticeship",
          permission: "Apprenticeship",
          className: "ant-submenu-custom-item",
          icon: <TbSchool />,
          label: (
            <Link to="/user/apprenticeship">
              <span>{t("Apprenticeship")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Shifts",
      className: "ant-menu-custom-parent-item",
      icon: <RiFileExcel2Fill />,
      label: <span>{t("Shifts")}</span>,

      children: [
        {
          key: "Shifts-planning",
          permission: "Shifts-planning",
          className: "ant-submenu-custom-item",
          icon: <LiaProjectDiagramSolid />,
          label: (
            <Link to="/user/shifts-planning">
              <span>{t("Shifts Planning")}</span>
            </Link>
          )
        },
        {
          key: "Sub-Shifts",
          permission: "Shifts",
          className: "ant-submenu-custom-item",
          icon: <GoTasklist />,
          label: (
            <Link to="/user/shifts">
              <span>{t("Shifts")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Client Services",
      className: "ant-menu-custom-parent-item",
      icon: <RiServiceFill />,
      label: <span>{t("Client Services")}</span>,
      children: [
        {
          key: "Clients",
          className: "ant-submenu-custom-item",
          permission: "Clients",
          icon: <GiBuyCard />,
          label: (
            <Link to="/user/clients">
              <span>{t("Clients")}</span>
            </Link>
          )
        },
        {
          key: "Pipelines",
          permission: "Pipelines",
          className: "ant-submenu-custom-item",
          icon: <SiGithubactions />,
          label: (
            <Link to="/user/pipelines">
              <span>{t("Pipelines")}</span>
            </Link>
          )
        },
        {
          key: "Calendar",
          permission: "Calendar",
          className: "ant-submenu-custom-item",
          icon: <FaUserClock />,
          label: (
            <Link to="/user/calendar">
              <span>{t("Appointments")}</span>
            </Link>
          )
        },
        {
          key: "Dispatch",
          permission: "Dispatch",
          className: "ant-submenu-custom-item",
          icon: <TbTruckDelivery />,
          label: (
            <Link to="/user/dispatch">
              <span>{t("Dispatch board")}</span>
            </Link>
          )
        },
        {
          key: "Reviews",
          permission: "Reviews",
          className: "ant-submenu-custom-item",
          icon: <TbStar />,
          label: (
            <Link to="/user/reviews">
              <span>{t("Reviews")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Financials",
      className: "ant-menu-custom-parent-item",
      icon: <RiExchangeFundsFill />,
      label: <span>{t("Financials")}</span>,
      children: [
        // {
        //   key: "Deals",
        //   permission: "Deals",
        //   className: "ant-submenu-custom-item",
        //   icon: <BsCurrencyExchange />,
        //   label: (
        //     <Link to="/user/deals">
        //       <span>{t("Deals")}</span>
        //     </Link>
        //   )
        // },

        {
          key: "Inventory",
          permission: "Inventory",
          className: "ant-submenu-custom-item",
          icon: <MdOutlineInventory2 />,
          label: (
            <Link to="/user/inventory">
              <span>{t("Inventory")}</span>
            </Link>
          )
        },
        {
          key: "Invoices",
          permission: "Invoices",
          className: "ant-submenu-custom-item",
          icon: <LiaFileInvoiceDollarSolid />,
          label: (
            <Link to="/user/invoices">
              <span>{t("Invoices")}</span>
            </Link>
          )
        },
        {
          key: "Offers",
          permission: "Invoices",
          className: "ant-submenu-custom-item",
          icon: <RiFileExcel2Fill />,
          label: (
            <Link to="/user/offers">
              <span>{t("Offers")}</span>
            </Link>
          )
        },
        {
          key: "Materials",
          permission: "Materials",
          className: "ant-submenu-custom-item",
          icon: <TbPackages />,
          label: (
            <Link to="/user/materials">
              <span>{t("Materials & articles")}</span>
            </Link>
          )
        },
        {
          key: "Find jobs",
          permission: "Invoices",
          className: "ant-submenu-custom-item",
          icon: <TbZoomMoney />,
          label: (
            <Link to="/user/offers/auftragssuche">
              <span>{t("Find jobs")}</span>
            </Link>
          )
        },
        {
          key: "Vendor-contracts",
          permission: "Vendor-contracts",
          className: "ant-submenu-custom-item",
          icon: <MdOutlineReceiptLong />,
          label: (
            <Link to="/user/vendor-contracts">
              <span>{t("Contracts & subscriptions")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Accounting",
      className: "ant-menu-custom-parent-item",
      icon: <TbReportMoney />,
      label: <span>{t("Accounting")}</span>,
      children: [
        {
          key: "Accounting-overview",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <RiDashboardFill />,
          label: (
            <Link to="/user/accounting">
              <span>{t("Finance overview")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-belege",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <MdOutlineReceiptLong />,
          label: (
            <Link to="/user/accounting/belege">
              <span>{t("Receipts inbox")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-bank",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <BsBank />,
          label: (
            <Link to="/user/accounting/bank">
              <span>{t("Bank & payments")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-receivables",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <LiaFileInvoiceDollarSolid />,
          label: (
            <Link to="/user/accounting/receivables">
              <span>{t("Open items & dunning")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-recurring",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <TbRepeat />,
          label: (
            <Link to="/user/accounting/recurring">
              <span>{t("Direct debit & retentions")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-suppliers",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <MdOutlineWarehouse />,
          label: (
            <Link to="/user/accounting/suppliers">
              <span>{t("Suppliers")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-journal",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <BiSpreadsheet />,
          label: (
            <Link to="/user/accounting/journal">
              <span>{t("Journal & accounts")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-tax",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <TbReceiptTax />,
          label: (
            <Link to="/user/accounting/tax">
              <span>{t("VAT & filings")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-payroll",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <FaPerson />,
          label: (
            <Link to="/user/accounting/payroll">
              <span>{t("Payroll runs")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-assets",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <GiOfficeChair />,
          label: (
            <Link to="/user/accounting/assets">
              <span>{t("Fixed assets & cash book")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-reports",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <AiOutlineFundProjectionScreen />,
          label: (
            <Link to="/user/accounting/reports">
              <span>{t("Reports & liquidity")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-datev",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <TbFileExport />,
          label: (
            <Link to="/user/accounting/datev">
              <span>{t("DATEV export")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-deadlines",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <FaUserClock />,
          label: (
            <Link to="/user/accounting/deadlines">
              <span>{t("Deadlines")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-assistant",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <GiArtificialIntelligence />,
          label: (
            <Link to="/user/accounting/assistant">
              <span>{t("Accounting assistant")}</span>
            </Link>
          )
        },
        {
          key: "Accounting-settings",
          permission: "Accounting",
          className: "ant-submenu-custom-item",
          icon: <AiOutlineControl />,
          label: (
            <Link to="/user/accounting/settings">
              <span>{t("Accounting settings")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Project Operations",
      className: "ant-menu-custom-parent-item",
      icon: <AiOutlineFundProjectionScreen />,
      label: <span>{t("Project Operations")}</span>,
      children: [
        {
          key: "Projects",
          permission: "Projects",
          className: "ant-submenu-custom-item",
          icon: <LiaProjectDiagramSolid />,
          label: (
            <Link to="/user/projects">
              <span>{t("Projects")}</span>
            </Link>
          )
        },
        {
          key: "Tasks",
          permission: "Tasks",
          className: "ant-submenu-custom-item",
          icon: <GoTasklist />,
          label: (
            <Link to="/user/tasks">
              <span>{t("Tasks")}</span>
            </Link>
          )
        },
        {
          key: "Measurements",
          permission: "Measurements",
          className: "ant-submenu-custom-item",
          icon: <MdOutlineSquareFoot />,
          label: (
            <Link to="/user/measurements">
              <span>{t("Measurements")}</span>
            </Link>
          )
        },
        {
          key: "Work-reports",
          permission: "Work-reports",
          className: "ant-submenu-custom-item",
          icon: <TbClipboardCheck />,
          label: (
            <Link to="/user/work-reports">
              <span>{t("Work reports")}</span>
            </Link>
          )
        },
        {
          key: "Subcontractors",
          permission: "Subcontractors",
          className: "ant-submenu-custom-item",
          icon: <TbUserShield />,
          label: (
            <Link to="/user/subcontractors">
              <span>{t("Subcontractors")}</span>
            </Link>
          )
        },
        {
          key: "Change-orders",
          permission: "Change-orders",
          className: "ant-submenu-custom-item",
          icon: <TbFilePlus />,
          label: (
            <Link to="/user/change-orders">
              <span>{t("Change orders")}</span>
            </Link>
          )
        },
        {
          key: "Site-docs",
          permission: "Site-docs",
          className: "ant-submenu-custom-item",
          icon: <TbCameraPlus />,
          label: (
            <Link to="/user/site-docs">
              <span>{t("Site documentation")}</span>
            </Link>
          )
        },
        {
          key: "Maintenance",
          permission: "Maintenance",
          className: "ant-submenu-custom-item",
          icon: <TbTool />,
          label: (
            <Link to="/user/maintenance">
              <span>{t("Maintenance & inspections")}</span>
            </Link>
          )
        },
        {
          key: "Funding",
          permission: "Funding",
          className: "ant-submenu-custom-item",
          icon: <TbPigMoney />,
          label: (
            <Link to="/user/funding">
              <span>{t("Funding")}</span>
            </Link>
          )
        },
        {
          key: "Site-diary",
          permission: "Site-diary",
          className: "ant-submenu-custom-item",
          icon: <TbNotebook />,
          label: (
            <Link to="/user/site-diary">
              <span>{t("Site diary")}</span>
            </Link>
          )
        },
        {
          key: "Warranty",
          permission: "Warranty",
          className: "ant-submenu-custom-item",
          icon: <TbShieldCheck />,
          label: (
            <Link to="/user/warranty">
              <span>{t("Acceptance & warranty")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Support",
      className: "ant-menu-custom-parent-item",
      icon: <MdSupportAgent />,
      label: <span>{t("Support")}</span>,
      children: [
        {
          key: "Boards",
          className: "ant-submenu-custom-item",
          permission: "Boards",
          icon: <IoIdCardOutline />,
          label: (
            <Link to="/user/Boards">
              <span>{t("Tickets")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Assests",
      className: "ant-menu-custom-parent-item",
      icon: <MdOutlineWarehouse />,
      label: <span>{t("Assests")}</span>,
      children: [
        {
          key: "Assests-controls",
          permission: "Assests-controls",
          className: "ant-submenu-custom-item",
          icon: <AiOutlineControl />,
          label: (
            <Link to="/user/assests-controls">
              <span>{t("Assests controls")}</span>
            </Link>
          )
        },
        {
          // Key matches the route segment, which is what the page's route guard checks.
          key: "Room-management",
          permission: "Room-management",
          className: "ant-submenu-custom-item",
          icon: <MdOutlineMeetingRoom />,
          label: (
            <Link to="/user/room-management">
              <span>{t("Room management")}</span>
            </Link>
          )
        }
      ]
    },
    {
      key: "Directory",
      className: "ant-menu-custom-item",
      permission: "Directory",
      icon: <IoDocumentLockSharp />,
      label: (
        <Link to="/user/directory">
          <span>{t("Directory")}</span>
        </Link>
      )
    },
    {
      key: "Ai",
      className: "ant-menu-custom-item",
      permission: "Ai",
      icon: <GiArtificialIntelligence />,
      label: (
        <Link to="/user/ai">
          <span>{t("AI")}</span>
        </Link>
      )
    },
    {
      key: "Settings",
      className: "ant-menu-custom-parent-item",
      icon: <MdMiscellaneousServices />,
      label: <span>{t("Settings")}</span>,
      children: [
        {
          key: "Predefinedvalues",
          className: "ant-submenu-custom-item",
          icon: <FaDiagramPredecessor />,
          permission: "Predefinedvalues",
          label: (
            <Link to="/user/predefinedvalues">
              <span>{t("Predefined Values")}</span>
            </Link>
          )
        },
        {
          key: "Roles",
          permission: "Roles",
          className: "ant-submenu-custom-item",
          icon: <MdOutlineSecurity />,
          label: (
            <Link to="/user/roles">
              <span>{t("Permissions")}</span>
            </Link>
          )
        },
        {
          // Owner only: no role grants "Settings" (the route guard's module for /user/settings/*)
          key: "Customer-messages",
          permission: "Settings",
          className: "ant-submenu-custom-item",
          icon: <TbMessage2 />,
          label: (
            <Link to="/user/settings/messaging">
              <span>{t("Customer messages")}</span>
            </Link>
          )
        },
        {
          // Owner only, like the other settings: assistant commands per user and month
          key: "Assistant-usage",
          permission: "Settings",
          className: "ant-submenu-custom-item",
          icon: <AudioOutlined />,
          label: (
            <Link to="/user/settings/assistant">
              <span>{t("Assistant usage")}</span>
            </Link>
          )
        }
      ]
    }
  ];

  useEffect(() => {
    const user = isAuthenticated();
    if (!user?.employeeId || user.role != 1) return;
    let active = true;
    loadEmployee(user.employeeId).then(data => active && setUserData(data));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let filteredItems = [];

    filteredItems = items
      ?.map(item => {
        if (
          item?.permission &&
          !hasPermission(userData?.roleId?.permissions, item?.permission, "Read", true)
        ) {
          return null;
        }
        if (item?.children) {
          const filteredChildren = item.children.filter(
            child =>
              !child.permission ||
              hasPermission(userData?.roleId?.permissions, child?.permission, "Read", true)
          );
          // A group with nothing the user may open is just an empty accordion; hide it.
          return filteredChildren.length ? { ...item, children: filteredChildren } : null;
        }
        return item;
      })
      .filter(Boolean);

    setMenuItems(filteredItems);
  }, [userData, i18n.language]);

  return menuItems;
};

const ServiceProviderSidebar = ({ collapsed, setLabel1, setLabel2, label2, label1 }) => {
  const location = useLocation();
  const pathused = location.pathname;
  const menuItems = useServiceProviderMenu();
  const label1Control = pathused === "/user" ? "" : label1;

  const selectkeysmenu = e => {
    setLabel1(e.keyPath[1]);
    setLabel2(e.keyPath[0]);
  };

    const activeKey =
      pathused === "/user"
        ? "Dashboard"
        : pathused === "/chats" || pathused === "/chat"
          ? "Chat"
          : label2;

    return (
      <>
        <Menu
          className="sidersidenav"
          selectedKeys={[activeKey]}
          defaultOpenKeys={[label1Control]}
          mode="inline"
          theme="light"
          inlineCollapsed={collapsed}
          items={menuItems}
          style={{ height: "100%" }}
          onSelect={selectkeysmenu}
        />
      </>
    );
};

export default ServiceProviderSidebar;
